import { Box, Flex, SimpleGrid } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { InputLabel, NumberInput, Select, Text, TextInput, YesNoRadioButton } from 'components';
import { CreditOptionsResponse, CreditRequestForm } from 'types';

interface CreditRequestRequestProps {
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
}

const CreditRequestRequest = ({ form, options, visibleFields, readOnly = false }: CreditRequestRequestProps) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box p={'md'}>
        <SimpleGrid cols={4}>
          <NumberInput
            label={t('credit.creditAmount.label')}
            key={form.key('creditAmount')}
            {...form.getInputProps('creditAmount')}
            min={options['creditAmount']?.min}
            max={options['creditAmount']?.max}
            required={true}
            disabled={readOnly}
          />
          <NumberInput
            label={t('credit.paymentPeriod.label')}
            key={form.key('paymentPeriod')}
            {...form.getInputProps('paymentPeriod')}
            suffix={undefined}
            min={options['paymentPeriod']?.min}
            max={options['paymentPeriod']?.max}
            allowDecimal={false}
            required={true}
            disabled={readOnly}
          />
          <Box>
            <InputLabel label={t('credit.monthlyInstallment.label')} required={true} />
            <Flex align={'center'} justify={'space-evenly'}>
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                {t('credit.monthlyInstallment.from')}
              </Text>
              <NumberInput
                key={form.key('monthlyInstallmentFrom')}
                {...form.getInputProps('monthlyInstallmentFrom')}
                suffix={undefined}
                min={options['existingMonthlyInstallment']?.min}
                max={options['existingMonthlyInstallment']?.max}
                disabled={true}
                w={'30%'}
              />
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                {t('credit.monthlyInstallment.to')}
              </Text>
              <NumberInput
                key={form.key('monthlyInstallmentTo')}
                {...form.getInputProps('monthlyInstallmentTo')}
                suffix={undefined}
                min={options['existingMonthlyInstallment']?.min}
                max={options['existingMonthlyInstallment']?.max}
                disabled={true}
                w={'30%'}
              />
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                {'CHF'}
              </Text>
            </Flex>
          </Box>
          <YesNoRadioButton
            label={t('credit.creditInsurance.label')}
            name={'creditInsurance'}
            form={form}
            t={t}
            required={true}
            disabled={readOnly}
          />
        </SimpleGrid>
        <SimpleGrid cols={4} mt={'sm'}>
          <Select
            label={t('credit.creditType.label')}
            data={options['creditType'].map((el) => ({
              value: el,
              label: t(`credit.creditType.options.${el}`),
            }))}
            key={form.key('creditType')}
            {...form.getInputProps('creditType')}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          {visibleFields.includes('creditPurpose') && (
            <Select
              label={t('credit.creditPurpose.label')}
              data={options['creditPurpose'].map((el) => ({
                value: el,
                label: t(`credit.creditPurpose.options.${el}`),
              }))}
              key={form.key('creditPurpose')}
              {...form.getInputProps('creditPurpose')}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('creditPurposeNote') && (
            <TextInput
              label={t('credit.creditPurposeNote.label')}
              key={form.key('creditPurposeNote')}
              {...form.getInputProps('creditPurposeNote')}
              style={{ verticalAlign: 'top' }}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>

      <Box p={'md'} mt={'sm'}>
        <SimpleGrid cols={4}>
          {visibleFields.includes('existingMonthlyInstallment') && (
            <NumberInput
              label={t('credit.existingMonthlyInstallment.label')}
              key={form.key('existingMonthlyInstallment')}
              min={options['existingMonthlyInstallment']?.min}
              max={options['existingMonthlyInstallment']?.max}
              {...form.getInputProps('existingMonthlyInstallment')}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('bank') && (
            <Select
              label={t('credit.bank.label')}
              data={options['bank'].map((el) => ({
                value: el,
                label: t(`credit.bank.options.${el}`),
              }))}
              key={form.key('bank')}
              {...form.getInputProps('bank')}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('bankName') && (
            <TextInput
              label={t('credit.bankName.label')}
              key={form.key('bankName')}
              {...form.getInputProps('bankName')}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('refinanceCreditCard') && (
            <YesNoRadioButton
              label={t('credit.refinanceCreditCard.label')}
              name={'refinanceCreditCard'}
              form={form}
              t={t}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('refinanceLease') && (
            <YesNoRadioButton
              label={t('credit.refinanceLease.label')}
              name={'refinanceLease'}
              form={form}
              t={t}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>
      <Box p={'md'} mt={'sm'}>
        <SimpleGrid cols={4}>
          <TextInput
            label={t('credit.comment.singular')}
            key={form.key('comment1')}
            {...form.getInputProps('comment1')}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default CreditRequestRequest;
